import type { SessionUser } from '@rahul365/shared';
import { Activity, BookOpen, ChartNoAxesCombined, Dumbbell, Search, Settings2, Utensils } from 'lucide-react';
import { NavLink, Outlet } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { CommandMenu } from '../components/CommandMenu.js';
import { useConnectivity } from '../hooks/useConnectivity.js';
import { useHealthConnectForegroundSync } from '../hooks/useHealthConnectForegroundSync.js';

const navigation = [
  { to: '/today', label: 'Today', icon: Activity },
  { to: '/train', label: 'Train', icon: Dumbbell },
  { to: '/food', label: 'Food', icon: Utensils },
  { to: '/progress', label: 'Progress', icon: ChartNoAxesCombined },
  { to: '/journal', label: 'Journal', icon: BookOpen },
];

export function AppShell({ user }: { user: SessionUser }) {
  const connectivity = useConnectivity();
  useHealthConnectForegroundSync();
  const [searchOpen, setSearchOpen] = useState(false);
  useEffect(() => {
    const openSearch = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener('keydown', openSearch);
    return () => window.removeEventListener('keydown', openSearch);
  }, []);
  return (
    <div className="app-shell">
      <aside className="sidebar">
        <NavLink className="brand" to="/today" aria-label="Rahul 365 home">
          <span className="brand-mark">R365</span>
          <span><strong>RAHUL 365</strong><small>Consistency &gt; perfection</small></span>
        </NavLink>
        <nav aria-label="Primary">
          {navigation.map(({ to, label, icon: Icon }) => (
            <NavLink key={to} to={to}><Icon aria-hidden="true" /> <span>{label}</span></NavLink>
          ))}
        </nav>
        <div className="sidebar__footer">
          <NavLink to="/integrations"><Settings2 aria-hidden="true" /> Integrations</NavLink>
          <NavLink to="/more"><span className="avatar">{user.displayName[0]}</span> {user.displayName}</NavLink>
        </div>
      </aside>

      <div className="app-content">
        <header className="mobile-header">
          <NavLink className="brand" to="/today"><span className="brand-mark">R365</span><strong>RAHUL 365</strong></NavLink>
          <div className="mobile-header__actions">
            <button className="icon-button" aria-label="Search" onClick={() => setSearchOpen(true)}><Search /></button>
            <NavLink className="avatar" to="/more" aria-label="Profile and menu">{user.displayName[0]}</NavLink>
          </div>
        </header>
        {(!connectivity.online || connectivity.pending > 0 || connectivity.syncing) && (
          <div className={`sync-banner ${connectivity.online ? '' : 'sync-banner--offline'}`} role="status">
            {!connectivity.online ? `Saved offline · ${connectivity.pending} waiting` : connectivity.syncing ? 'Syncing…' : `${connectivity.pending} waiting to sync`}
          </div>
        )}
        <main className="page"><Outlet /></main>
      </div>

      <nav className="bottom-nav" aria-label="Primary">
        {navigation.map(({ to, label, icon: Icon }) => (
          <NavLink key={to} to={to}><Icon aria-hidden="true" /><span>{label}</span></NavLink>
        ))}
      </nav>
      {searchOpen ? <CommandMenu onClose={() => setSearchOpen(false)} /> : null}
    </div>
  );
}
